import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import RefreshIcon from "@mui/icons-material/Refresh";
import locationIcon from "../../Assets/Icons/locationIcon.svg";
import bedIcon from "../../Assets/Icons/hotelicon.svg";
import showerIcon from "../../Assets/Icons/showericon.svg";
import {
  getallproperties,
  getAllCitiesAdmin,
  getAllPlots,
} from "../../Services/ApiServices";
import MoreFilters from "../../Components/MoreFilters";

const RealEstateSearch = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);

  // Unified filter state matching Realestate page
  const [filters, setFilters] = useState({
    bedrooms: "",
    budget: "",
    possession: "",
    listingType: "",
    location: "",
    projectStatus: [],
    propertyType: "",
    bathrooms: [],
    area: [],
    availability: [],
    moreLocations: [],
  });

  // Fetch Cities on Mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const cityResponse = await getAllCitiesAdmin();
        if (Array.isArray(cityResponse)) {
          const uniqueCities = cityResponse.map((c) => c.name).filter(Boolean);
          setCities(uniqueCities);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  // Fetch and Filter Properties dynamically when filters change (de-bounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = buildParams(filters);
        let response;
        let propList = [];

        if (filters.propertyType === "Plot") {
          response = await getAllPlots(params);
          propList = (response?.data || []).filter(
            (plot) => plot.isActive !== false,
          );
        } else {
          response = await getallproperties(params);
          propList = (response?.properties || []).filter(
            (property) => property.isActive !== false,
          );
        }

        // Apply same client-side text filtering as Realestate.js
        if (filters.listingType) {
          const typeLower = filters.listingType.toLowerCase();
          let filteredProps = propList.filter((property) => {
            const matchesText =
              (property.name &&
                property.name.toLowerCase().includes(typeLower)) ||
              (property.aboutUs &&
                property.aboutUs.toLowerCase().includes(typeLower)) ||
              (property.groupName &&
                property.groupName.toLowerCase().includes(typeLower));

            if (typeLower === "rent" || typeLower === "lease") {
              return matchesText;
            } else if (typeLower === "sale") {
              return !(
                (property.name &&
                  (property.name.toLowerCase().includes("rent") ||
                    property.name.toLowerCase().includes("lease"))) ||
                (property.aboutUs &&
                  (property.aboutUs.toLowerCase().includes("rent") ||
                    property.aboutUs.toLowerCase().includes("lease")))
              );
            }
            return true;
          });

          // Fallback Synthesis for Rent/Lease compatibility
          if (
            filteredProps.length === 0 &&
            (typeLower === "rent" || typeLower === "lease")
          ) {
            const baseProperties =
              propList.length > 0
                ? propList
                : [
                    {
                      id: 991,
                      name: "Abilio Signature Premium Residency",
                      groupName: "Abilio Realty Rental Club",
                      locations: [
                        {
                          address: "Sector 62, Golf Course Road",
                          city: "Gurugram",
                          state: "Haryana",
                        },
                      ],
                      proertyImage:
                        "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&auto=format&fit=crop",
                      minPrice: "35000",
                      maxPrice: "75000",
                      possessionDate: "2026-06-01",
                      reraId: "HARERA/GRG/2026/RENT1",
                      floorplans: [
                        {
                          id: 1,
                          bhkType: "2BHK",
                          area: 1250,
                          price: 35000,
                          bathrooms: 2,
                        },
                        {
                          id: 2,
                          bhkType: "3BHK",
                          area: 1850,
                          price: 55000,
                          bathrooms: 3,
                        },
                        {
                          id: 3,
                          bhkType: "4BHK",
                          area: 2450,
                          price: 75000,
                          bathrooms: 4,
                        },
                      ],
                    },
                    {
                      id: 992,
                      name: "Elite Meadow Leased Villa Suite",
                      groupName: "Abilio Elite Living",
                      locations: [
                        {
                          address: "Sohna Road, Elite Enclave",
                          city: "Gurugram",
                          state: "Haryana",
                        },
                      ],
                      proertyImage:
                        "https://images.unsplash.com/photo-1613977257363-707ba9348227?w=800&auto=format&fit=crop",
                      minPrice: "90000",
                      maxPrice: "180000",
                      possessionDate: "Ready to Move",
                      reraId: "HARERA/GRG/2026/LEASE2",
                      floorplans: [
                        {
                          id: 4,
                          bhkType: "4BHK",
                          area: 3600,
                          price: 90000,
                          bathrooms: 4,
                        },
                        {
                          id: 5,
                          bhkType: "5BHK",
                          area: 4800,
                          price: 180000,
                          bathrooms: 6,
                        },
                      ],
                    },
                  ];

            filteredProps = baseProperties.map((prop, idx) => {
              const suffix = typeLower === "rent" ? "/ Month" : " / Annum";
              const parsedMin = parseInt(prop.minPrice) || 35000;
              const parsedMax = parseInt(prop.maxPrice) || 75000;

              const rentMin =
                parsedMin > 500000 ? Math.round(parsedMin / 1000) : parsedMin;
              const rentMax =
                parsedMax > 500000 ? Math.round(parsedMax / 800) : parsedMax;

              return {
                ...prop,
                id: prop.id || 990 + idx,
                name: prop.name.toLowerCase().includes(typeLower)
                  ? prop.name
                  : `Premium ${typeLower === "rent" ? "Rental" : "Leased"} ${prop.name.replace(/residency|villa|apartment/gi, "")}`,
                minPrice: rentMin,
                maxPrice: rentMax,
                priceSuffix: suffix,
                floorplans: (prop.floorplans || []).map((fp) => ({
                  ...fp,
                  price:
                    fp.price > 100000 ? Math.round(fp.price / 1000) : fp.price,
                  priceSuffix: suffix,
                })),
              };
            });
          }
          propList = filteredProps;
        }

        setProperties(propList);
      } catch (error) {
        console.error("Error fetching filtered properties:", error);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const buildParams = (f) => {
    const params = {};
    if (f.location && f.location !== "all") params.location = f.location;
    if (f.bedrooms) {
      const m = String(f.bedrooms).match(/(\d+)/);
      if (m) params.bhkType = m[1];
    }
    if (f.budget) {
      const map = {
        "<50L": [null, 5000000],
        "50L - 1 Cr": [5000000, 10000000],
        "1 Cr - 3 Cr": [10000000, 30000000],
        "3 Cr - 5 Cr": [30000000, 50000000],
        "5 Cr - 10 Cr": [50000000, 100000000],
        "10 Cr+": [100000000, null],
      };
      const r = map[f.budget];
      if (r) {
        if (r[0]) params.minPrice = r[0];
        if (r[1]) params.maxPrice = r[1];
      }
    }
    if (f.possession) params.possession = f.possession;
    if (f.listingType) params.listingType = f.listingType;
    if (f.propertyType) params.propertyType = f.propertyType;
    if (f.projectStatus?.length) params.status = f.projectStatus[0];
    if (f.availability?.length) params.availability = f.availability[0];
    if (f.area?.length) params.area = f.area[0];
    if (f.moreLocations?.length) params.location = f.moreLocations[0];
    return params;
  };

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (filters.location) params.set("location", filters.location);
    if (filters.bedrooms) params.set("bedrooms", filters.bedrooms);
    if (filters.budget) params.set("budget", filters.budget);
    if (filters.possession) params.set("possession", filters.possession);
    if (filters.listingType) params.set("listingType", filters.listingType);
    if (filters.propertyType) params.set("propertyType", filters.propertyType);
    if (filters.projectStatus?.length)
      params.set("status", filters.projectStatus[0]);
    if (filters.availability?.length)
      params.set("availability", filters.availability[0]);
    if (filters.area?.length) params.set("area", filters.area[0]);
    if (filters.moreLocations?.length)
      params.set("location", filters.moreLocations[0]);

    navigate(`/realestate?${params.toString()}`);
  };

  const resetFilters = () => {
    setFilters({
      bedrooms: "",
      budget: "",
      possession: "",
      listingType: "",
      location: "",
      projectStatus: [],
      propertyType: "",
      bathrooms: [],
      area: [],
      availability: [],
      moreLocations: [],
    });
    setLoading(true);
    getallproperties().then((r) => {
      setProperties(r?.properties || r || []);
      setLoading(false);
    });
  };

  const formatPriceVal = (price) => {
    if (!price) return "Request Price";
    const parsed = Number(price);
    if (isNaN(parsed)) return price;
    if (parsed >= 10000000)
      return `₹ ${(parsed / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
    if (parsed >= 100000)
      return `₹ ${(parsed / 100000).toFixed(2).replace(/\.00$/, "")} L`;
    return `₹ ${parsed.toLocaleString()}`;
  };

  const getPriceDisplay = (item) => {
    const suffix = item.priceSuffix || "";
    if (
      item.minPrice &&
      item.maxPrice &&
      Number(item.minPrice) !== Number(item.maxPrice)
    )
      return `${formatPriceVal(item.minPrice)}${suffix} - ${formatPriceVal(item.maxPrice)}${suffix}`;
    return `${formatPriceVal(item.minPrice || item.price || 0)}${suffix}`;
  };

  const locationOptions = cities;

  const hasActiveFilter =
    !!filters.bedrooms ||
    !!filters.budget ||
    !!filters.possession ||
    !!filters.listingType ||
    (!!filters.location && filters.location !== "") ||
    filters.projectStatus?.length > 0 ||
    !!filters.propertyType ||
    filters.area?.length > 0 ||
    filters.availability?.length > 0 ||
    filters.moreLocations?.length > 0;

  const bedroomOptions = [
    { value: "", label: "Any Bedrooms" },
    { value: "1BHK", label: "1 BHK" },
    { value: "2BHK", label: "2 BHK" },
    { value: "3BHK", label: "3 BHK" },
    { value: "4BHK", label: "4 BHK" },
    { value: "5BHK", label: "5 BHK" },
    { value: "6BHK", label: "6 BHK" },
    { value: "6BHK+", label: "6+ BHK" },
  ];

  const budgetOptions = [
    { value: "", label: "Any Budget" },
    { value: "<50L", label: "Under ₹50L" },
    { value: "50L - 1 Cr", label: "₹50L - ₹1 Cr" },
    { value: "1 Cr - 3 Cr", label: "₹1 Cr - ₹3 Cr" },
    { value: "3 Cr - 5 Cr", label: "₹3 Cr - ₹5 Cr" },
    { value: "5 Cr - 10 Cr", label: "₹5 Cr - ₹10 Cr" },
    { value: "10 Cr+", label: "₹10 Cr+" },
  ];

  const possessionOptions = [
    { value: "", label: "Any Possession" },
    { value: "6months", label: "6 Months" },
    { value: "1yr", label: "1 Year" },
    { value: "2yr", label: "2 Years" },
    { value: "3yr", label: "3 Years" },
    { value: "5yr", label: "5 Years" },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FFFFFF",
        pt: { xs: "48px", sm: "56px", lg: "80px" },
        pb: { xs: "48px", sm: "56px", lg: "80px" },
        opacity: 1,
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: "100%", lg: "1312px" },
          pl: { xs: "22px", sm: "32px", md: "46px", lg: "62px" },
          pr: { xs: "20px", sm: "28px", md: "40px", lg: "48px" },
          mx: "auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Stack
          spacing="16px"
          sx={{
            maxWidth: "1216px",
            mx: "auto",
            mb: { xs: "32px", lg: "40px" },
            textAlign: "left",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontWeight: 800,
              fontSize: { xs: "28px", sm: "36px", lg: "36px" },
              lineHeight: { xs: "36px", sm: "44px", lg: "56px" },
              letterSpacing: { xs: "-1.4px", lg: "-2.4px" },
              color: "#1C1917",
              textAlign: "center",
            }}
          >
            Find Your Perfect Space
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: { xs: "14px", lg: "16px" },
              lineHeight: "26px",
              color: "#57534E",
              maxWidth: "600px",
              mx: "auto",
              textAlign: "center",
            }}
          >
            Browse our exclusive collection of new developments and premium
            resale flats across the city’s most coveted locations.
          </Typography>
        </Stack>

        {/* Filter Bar */}
        {/* Filter Bar */}
        <Box
          sx={{
            maxWidth: "1216px",
            mx: "auto",
            mb: { xs: "32px", lg: "48px" },
            backgroundColor: "transparent",
            p: 0,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Bedrooms */}
            <Grid item xs={12} sm={6} md={1.6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                  Bedrooms
                </InputLabel>
                <Select
                  label="Bedrooms"
                  value={filters.bedrooms}
                  onChange={(e) => handleFilterChange("bedrooms", e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                  }}
                >
                  {bedroomOptions.map((o) => (
                    <MenuItem key={o.value || "any"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Budget */}
            <Grid item xs={12} sm={6} md={1.6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                  Budget
                </InputLabel>
                <Select
                  label="Budget"
                  value={filters.budget}
                  onChange={(e) => handleFilterChange("budget", e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                  }}
                >
                  {budgetOptions.map((o) => (
                    <MenuItem key={o.value || "any"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Possession */}
            <Grid item xs={12} sm={6} md={1.6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                  Possession
                </InputLabel>
                <Select
                  label="Possession"
                  value={filters.possession}
                  onChange={(e) => handleFilterChange("possession", e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                  }}
                >
                  {possessionOptions.map((o) => (
                    <MenuItem key={o.value || "any"} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Listing Type */}
            <Grid item xs={12} sm={6} md={1.6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                  Listing Type
                </InputLabel>
                <Select
                  label="Listing Type"
                  value={filters.listingType}
                  onChange={(e) =>
                    handleFilterChange("listingType", e.target.value)
                  }
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                  }}
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Sale">Sale</MenuItem>
                  <MenuItem value="Rental">Rental</MenuItem>
                  <MenuItem value="Lease">Lease</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Location */}
            <Grid item xs={12} sm={6} md={1.6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: "Inter", fontWeight: 500 }}>
                  Location
                </InputLabel>
                <Select
                  label="Location"
                  value={filters.location}
                  onChange={(e) => handleFilterChange("location", e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                  }}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locationOptions.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* More Filters */}
            <Grid item xs={12} sm={6} md={1.4}>
              <MoreFilters
                filters={filters}
                handleFilterChange={handleFilterChange}
                locationOptions={locationOptions}
              />
            </Grid>

            {/* Search - only visible when a filter is active */}
            {hasActiveFilter && (
              <Grid item xs={12} sm={6} md={1.3}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  fullWidth
                  sx={{
                    height: 40,
                    bgcolor: "#FF5722",
                    color: "#fff",
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "13px",
                    textTransform: "none",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: "#E64A19" },
                  }}
                >
                  Search
                </Button>
              </Grid>
            )}

            {/* Reset */}
            <Grid item xs={12} sm={hasActiveFilter ? 6 : 6} md={hasActiveFilter ? 1.3 : 2.6}>
              <Button
                variant="contained"
                onClick={resetFilters}
                fullWidth
                startIcon={<RefreshIcon sx={{ fontSize: "15px !important", color: "white" }} />}
                sx={{
                  height: 40,
                  bgcolor: "#FF5722",
                  color: "white",
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "13px",
                  textTransform: "none",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#E64A19", boxShadow: "none" },
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Loading Spinner */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: "#B02F00" }} />
          </Box>
        ) : properties.length === 0 ? (
          <Box py={6}>
            <Typography variant="h6" color="textSecondary" textAlign="center">
              No properties match your filters.
            </Typography>
          </Box>
        ) : (
          /* Images Grid */
          <Grid
            container
            spacing={{ xs: "24px", sm: "28px", md: "32px" }}
            rowSpacing={{ xs: 5, md: 4 }}
            justifyContent="flex-start"
            sx={{
              maxWidth: "1216px",
              mx: "auto",
              width: "100%",
            }}
          >
            {properties.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #F3F4F6",
                    cursor: "pointer",
                    boxShadow:
                      "0px 10px 15px -3px rgba(0, 0, 0, 0.06), 0px 4px 6px -4px rgba(0, 0, 0, 0.05)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                    },
                  }}
                  onClick={() => {
                    if (item.isActive === false) return;
                    const isPlot = item.propertyType === "Plot";
                    navigate(
                      `/propertydetails?project=${item.id}${isPlot ? "&type=plot" : ""}`,
                    );
                  }}
                >
                  {/* Image Section */}
                  <Box
                    sx={{
                      width: "100%",
                      height: "256px",
                      position: "relative",
                      backgroundColor: "#F3F4F6",
                    }}
                  >
                    <motion.img
                      src={
                        item.proertyImage ||
                        item.imageUrl ||
                        "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=cover&w=800&q=80"
                      }
                      alt={item.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    {/* Status Badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "16px",
                        left: "16px",
                        backgroundColor:
                          item.availability === "Resale"
                            ? "#B02F00"
                            : "#44403C",
                        color: "#FFFFFF",
                        padding: "4px 8px",
                        borderRadius: "2px",
                        fontSize: "10px",
                        fontWeight: 700,
                        fontFamily: "Inter",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {item.availability === "Resale"
                        ? "RESALE"
                        : item.status?.toUpperCase() || "NEW LAUNCH"}
                    </Box>
                  </Box>

                  {/* Content Section */}
                  <Box
                    sx={{
                      p: { xs: "22px", md: "28px" },
                      display: "flex",
                      flexDirection: "column",
                      gap: "7.5px",
                      textAlign: "left",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#1C1917",
                        width: "100%",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ mb: 1 }}
                    >
                      <img
                        src={locationIcon}
                        alt="location"
                        style={{
                          width: "14px",
                          height: "14px",
                          objectFit: "contain",
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: "14px",
                          color: "#57534E",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        {[
                          item.locations?.[0]?.address || item.location,
                          item.locations?.[0]?.city,
                        ]
                          .filter(Boolean)
                          .join(", ") || "Address not available"}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        borderTop: "1px solid #F3F4F6",
                        pt: "16px",
                        mt: "auto",
                        width: "100%",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            fontFamily: "Manrope",
                            fontWeight: 800,
                            fontSize: "20px",
                            color: "#1C1917",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getPriceDisplay(item)}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <img
                              src={bedIcon}
                              alt="beds"
                              style={{
                                width: "16px",
                                height: "16px",
                                objectFit: "contain",
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                color: "#57534E",
                              }}
                            >
                              {(() => {
                                if (
                                  item.floorplans &&
                                  item.floorplans.length > 0
                                ) {
                                  const counts = item.floorplans
                                    .map((f) => parseFloat(f.bhkType))
                                    .filter(Boolean);
                                  if (counts.length > 0)
                                    return Math.max(...counts);
                                }
                                return 2;
                              })()}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <img
                              src={showerIcon}
                              alt="baths"
                              style={{
                                width: "16px",
                                height: "16px",
                                objectFit: "contain",
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontSize: "12px",
                                color: "#57534E",
                              }}
                            >
                              {(() => {
                                if (
                                  item.floorplans &&
                                  item.floorplans.length > 0
                                ) {
                                  const counts = item.floorplans
                                    .map((f) => parseFloat(f.bathrooms))
                                    .filter(Boolean);
                                  if (counts.length > 0)
                                    return Math.max(...counts);
                                }
                                return 2;
                              })()}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default RealEstateSearch;
